/**
 * Clipboard Serialization
 *
 * Functions for serializing tasks to clipboard format.
 */

import { FOKUS_CLIPBOARD_VERSION, omitEmpty, cleanIcon, buildTagsArray } from '../constants';
import { calculateRelativePositions } from '../positions';
import { isV2Format } from '../../../../shared/ConditionsDropdown/utils/conditionHelpers';
import { getWaitSeconds } from '../../../../shared/ConditionsDropdown/utils/conditionWait';
import { DEFAULT_OPTIONS } from '../../../TaskNode/DecisionOptionsModal';
import {
  serializeDependsOnIndex,
  serializeAttachedDocs,
  serializeDocTicks,
  serializeDocFiles,
  serializeVariableValues,
  serializeMilestoneAnchor,
  serializeNoteAnchor,
} from './payload';
import { serializeNestedSprintEntry } from './nestedSprint';

/**
 * Serialize tasks for clipboard (v2 minimal schema)
 * @param {Array} tasks - Tasks to serialize
 * @param {Object} relativePositions - live node positions; used for relative `pos` tuples
 * @param {Object} nestedSprintData - Nested sprint data (keyed by task.id)
 * @param {Array} tagDefinitions - Optional tag definitions with colors from the source sprint
 * @param {Array} customFieldDefinitions - Optional custom field definitions from the source campaign
 */
export function serializeForClipboard(tasks, nodePositions, nestedSprintData, tagDefinitions = [], customFieldDefinitions = []) {
  // Build a map of task.id -> array index for dependency remapping
  const taskIdToIndex = new Map();
  tasks.forEach((task, idx) => {
    taskIdToIndex.set(task.id, idx);
  });
  const relativePositions = calculateRelativePositions(tasks, nodePositions || {});
  const hasRelativeOffset = Object.values(relativePositions).some(
    (pos) => pos && (pos.x !== 0 || pos.y !== 0),
  );

  // Collect all unique tag names used in the tasks being copied
  const usedTagNames = new Set();
  tasks.forEach(task => {
    if (Array.isArray(task.tags)) {
      task.tags.forEach(tag => usedTagNames.add(tag));
    } else if (task.category) {
      usedTagNames.add(task.category);
    }
  });

  // Filter tag definitions to only include tags used in copied tasks
  const relevantTagDefs = tagDefinitions
    .filter(t => usedTagNames.has(t.name))
    .map(t => ({ name: t.name, color: t.color }));

  // Collect all custom field IDs used in the tasks being copied
  const usedCustomFieldIds = new Set();
  tasks.forEach(task => {
    if (task.customFieldValues) {
      Object.keys(task.customFieldValues).forEach(fieldId => {
        const value = task.customFieldValues[fieldId];
        if (value !== null && value !== undefined && value !== '') {
          usedCustomFieldIds.add(fieldId);
        }
      });
    }
  });

  // Filter custom field definitions to only include fields used in copied tasks
  // Build maps for converting IDs to indices
  const relevantCustomFields = customFieldDefinitions.filter(f => usedCustomFieldIds.has(f.id));
  const customFieldIdToIndex = new Map();
  const customFieldOptionIdToIndex = new Map(); // "fieldId-optionId" -> optionIndex

  relevantCustomFields.forEach((field, fieldIdx) => {
    customFieldIdToIndex.set(field.id, fieldIdx);
    if (field.type === 'select' && field.options) {
      field.options.forEach((opt, optIdx) => {
        customFieldOptionIdToIndex.set(`${field.id}-${opt.id}`, optIdx);
      });
    }
  });

  // Serialize custom field definitions without IDs (use array position instead)
  const relevantCustomFieldDefs = relevantCustomFields.map(f => omitEmpty({
    // No id field - array index is the identifier
    name: f.name,
    type: f.type,
    // No order field - array position is the order
    options: f.type === 'select' ? (f.options || []).map(opt => omitEmpty({
      // No id field - array index is the identifier
      label: opt.label,
      color: opt.color,
    })) : undefined,
  }));

  // Serialize tasks with minimal schema
  const serializedTasks = tasks.map((task, idx) => {
    // Convert dependencies to array indices
    const dependsOnIndices = (Array.isArray(task.dependsOn) ? task.dependsOn : [])
      .map(dep => {
        const depTaskId = typeof dep === 'string' ? dep : dep.taskId;
        const depIndex = taskIdToIndex.get(depTaskId);
        if (depIndex === undefined) return null; // dependency outside copied set
        return serializeDependsOnIndex(dep, depIndex, () => tasks.find(t => t.id === depTaskId));
      })
      .filter(d => d !== null);

    // Convert unlock conditions to clipboard format (indices instead of IDs)
    let unlockConditionsIndices;
    if (isV2Format(task.unlockConditions)) {
      const serializedBlocks = task.unlockConditions.blocks.map(block => {
        const serializedConds = block.conditions.map(cond => {
          const depIndex = taskIdToIndex.get(cond.taskId);
          if (depIndex === undefined) return null;
          let branchIdx;
          if (cond.branch) {
            const depTask = tasks.find(t => t.id === cond.taskId);
            const depOptions = depTask?.options?.length > 0 ? depTask.options : DEFAULT_OPTIONS;
            const idx = depOptions.findIndex(o => o.id === cond.branch);
            if (idx >= 0) branchIdx = idx;
          }
          return omitEmpty({ task: depIndex, branch: branchIdx, waitSeconds: getWaitSeconds(cond) });
        }).filter(Boolean);
        return omitEmpty({
          conditions: serializedConds.length > 0 ? serializedConds : undefined,
          innerOperator: block.innerOperator,
          outerOperator: block.outerOperator,
        });
      }).filter(b => b.conditions && b.conditions.length > 0);
      unlockConditionsIndices = serializedBlocks.length > 0
        ? { version: 2, blocks: serializedBlocks }
        : undefined;
    } else {
      const flatIndices = (task.unlockConditions || [])
        .map(cond => {
          const depIndex = taskIdToIndex.get(cond.taskId);
          if (depIndex === undefined) return null;
          let branchIdx;
          if (cond.branch) {
            const depTask = tasks.find(t => t.id === cond.taskId);
            const depOptions = depTask?.options?.length > 0 ? depTask.options : DEFAULT_OPTIONS;
            const idx = depOptions.findIndex(o => o.id === cond.branch);
            if (idx >= 0) branchIdx = idx;
          }
          return omitEmpty({ task: depIndex, operator: cond.operator, branch: branchIdx, waitSeconds: getWaitSeconds(cond) });
        })
        .filter(c => c !== null);
      unlockConditionsIndices = flatIndices.length > 0 ? flatIndices : undefined;
    }

    // Convert custom field values to use indices
    // From: { "cf-123": "opt-456" } -> { "0": 2 } (field index 0, option index 2)
    let indexedCustomFieldValues;
    if (task.customFieldValues && Object.keys(task.customFieldValues).length > 0) {
      indexedCustomFieldValues = {};
      for (const [fieldId, value] of Object.entries(task.customFieldValues)) {
        if (value === null || value === undefined || value === '') continue;
        const fieldIndex = customFieldIdToIndex.get(fieldId);
        if (fieldIndex === undefined) continue; // field not in copied set

        // For select fields, convert option ID to index
        const field = relevantCustomFields.find(f => f.id === fieldId);
        if (field?.type === 'select' && typeof value === 'string') {
          const optionIndex = customFieldOptionIdToIndex.get(`${fieldId}-${value}`);
          if (optionIndex !== undefined) {
            indexedCustomFieldValues[String(fieldIndex)] = optionIndex;
          }
        } else {
          // For other field types (text, number, date, checkbox), keep value as-is
          indexedCustomFieldValues[String(fieldIndex)] = value;
        }
      }
      if (Object.keys(indexedCustomFieldValues).length === 0) {
        indexedCustomFieldValues = undefined;
      }
    }

    const attachedDocs = serializeAttachedDocs(task);

    // Build task object, omitting defaults and empty values
    // Note: status 'todo' is the default, so omit it to save tokens
    const serializedTask = omitEmpty({
      name: task.name,
      // Only include status if not 'todo' or 'pending' (the defaults)
      status: (task.status && task.status !== 'todo' && task.status !== 'pending') ? task.status : undefined,
      // Only include nodeType if not 'task' (the default)
      nodeType: task.nodeType && task.nodeType !== 'task' ? task.nodeType : undefined,
      priority: task.priority,
      difficulty: task.difficulty,
      // No category field - use tags with first tag being primary
      tags: buildTagsArray(task),
      icon: cleanIcon(task.icon),
      notes: task.notes,
      internalNotes: task.internalNotes,
      dueDate: task.dueDate,
      estimatedDurationMinutes: task.estimatedDurationMinutes,
      reminders: (task.reminders || []).length > 0 ? task.reminders : undefined,
      customFieldValues: indexedCustomFieldValues,
      // Subtasks - only include if non-empty
      subtasks: (task.subtasks || []).length > 0
        ? task.subtasks.map(st => omitEmpty({ name: st.name, completed: st.completed || undefined }))
        : undefined,
      // Deliverables - only include if non-empty
      deliverables: (task.deliverables || []).length > 0
        ? task.deliverables.map(d => omitEmpty({ name: d.name, completed: d.completed || undefined }))
        : undefined,
      // Inputs - only include if non-empty (exclude mission-type inputs)
      inputs: (task.inputs || []).filter(i => i.sourceType !== 'mission').length > 0
        ? task.inputs.filter(i => i.sourceType !== 'mission').map(i => omitEmpty({ name: i.name, completed: i.completed || undefined }))
        : undefined,
      // Decision node options (fall back to DEFAULT_OPTIONS so branch colors are preserved)
      options: task.nodeType === 'decision'
        ? (task.options?.length > 0 ? task.options : DEFAULT_OPTIONS).map(opt => omitEmpty({ label: opt.label, color: opt.color }))
        : undefined,
      // Decision answer/selectedOption - convert option ID to option index
      answer: (() => {
        if (task.nodeType !== 'decision' || !task.answer) return undefined;
        const opts = task.options?.length > 0 ? task.options : DEFAULT_OPTIONS;
        const idx = opts.findIndex(o => o.id === task.answer);
        return idx >= 0 ? idx : undefined;
      })(),
      waitFor: task.nodeType === 'wait' ? (task.waitFor || 'email') : undefined,
      waitDetails: task.nodeType === 'wait' && task.waitDetails ? task.waitDetails : undefined,
      selectedOption: (() => {
        if (task.nodeType !== 'decision' || !task.selectedOption) return undefined;
        const opts = task.options?.length > 0 ? task.options : DEFAULT_OPTIONS;
        const idx = opts.findIndex(o => o.id === task.selectedOption);
        return idx >= 0 ? idx : undefined;
      })(),
      // Attached docs (by reference) and their per-mission checkbox overrides
      attachedDocs,
      docTicks: serializeDocTicks(task, attachedDocs),
      docFiles: serializeDocFiles(task, attachedDocs),
      variableValues: serializeVariableValues(task),
      // Migration marker rides along so a pasted copy of a migrated mission
      // doesn't re-run the legacy notes→docs migration (which would mint a
      // fresh doc from the frozen pre-migration content on every paste)
      docsMigrated: task.docsMigratedAt ? true : undefined,
      // Milestone host attachment (host task as array index)
      milestoneAnchor: serializeMilestoneAnchor(task, taskIdToIndex),
      noteAnchor: serializeNoteAnchor(task, taskIdToIndex),
      // Dependencies as array indices
      dependsOn: dependsOnIndices.length > 0 ? dependsOnIndices : undefined,
      unlockConditions: unlockConditionsIndices || undefined,
      // Nested sprint flag - only if true
      hasNestedSprint: task.nestedSprintId ? true : undefined,
      // Relative layout for Paste Here (top-left of the copied set is 0,0)
      pos: hasRelativeOffset && relativePositions[task.id]
        ? [Math.round(relativePositions[task.id].x), Math.round(relativePositions[task.id].y)]
        : undefined,
    });

    return serializedTask;
  });

  // Serialize nested sprints (including nested-within-nested) for top-level copied tasks only
  const serializedNestedSprints = tasks
    .map((task, idx) => {
      if (!task.nestedSprintId || !nestedSprintData[task.id]) return null;
      return serializeNestedSprintEntry(nestedSprintData[task.id], idx, nestedSprintData);
    })
    .filter(Boolean);

  // Build final clipboard object, omitting empty arrays
  return omitEmpty({
    fokus: FOKUS_CLIPBOARD_VERSION,
    tagDefinitions: relevantTagDefs.length > 0 ? relevantTagDefs : undefined,
    customFieldDefinitions: relevantCustomFieldDefs.length > 0 ? relevantCustomFieldDefs : undefined,
    tasks: serializedTasks,
    nestedSprints: serializedNestedSprints.length > 0 ? serializedNestedSprints : undefined,
  });
}
